/* tslint:disable:max-line-length */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { SendOutlined, CloseOutlined } from '@ant-design/icons';

import { Button, Heading, Spinner } from '@mm/components';
import { EmailConfig, WidgetTypes } from '@mm/state/model/WidgetModel';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';

import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';

import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import css from './SlideoutEmailStats.module.css';
import { errorToast } from '../../../../../common/helpers/toast';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../common/components/TableGrid/TableGrid';
import { formatDateTime } from '../../../../../common/helpers/formatDateTime';
import classNames from 'classnames';
import pluralize from 'pluralize';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { useHistory } from 'react-router-dom';
import { PaginationNav } from '../../../../../common/components/PaginationNav/PaginationNav';
import { useLiveChatService } from '../../../../features/liveChat/hooks/use-live-chat-service';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { ReactComponent as EmptyStats } from './empty-stats.svg';
import { selectEmailAnalytics } from '@mm/state/selectors/botAnalyticsSelector';

export interface SlideoutEmailStats {
  className?: string;
  chatbotId?: number;
  dialogueId?: number;
  emailWidgetId?: number;
}

const renderNumberCell =
  field =>
  ({ item }) =>
    <GridCell>{item[field] || 0}</GridCell>;

const cells = [
  {
    label: 'Email',
    keyName: 'email',
    Cell: ({ item }) => (
      <GridCell className={css.emailCell}>{item.email}</GridCell>
    ),
  },
  {
    label: 'Opened',
    keyName: 'open',
    Cell: renderNumberCell('open'),
  },
  {
    label: 'Clicked',
    keyName: 'click',
    Cell: renderNumberCell('click'),
  },
  {
    label: 'Sent',
    keyName: 'sent',
    Cell: ({ key, item }) => (
      <GridCell className={css.sentCell}>
        {formatDateTime(item.sent, 'MM/DD/YYYY, h:mm:ss a')}
      </GridCell>
    ),
  },
  {
    label: '',
    keyName: 'actions',
    Cell: ({ onInboxClick }) => (
      <GridCell>
        <Button
          css={{ container: css.inboxBtn }}
          color="secondary"
          size="sm"
          onClick={onInboxClick}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.8 7.96h-.85a.32.32 0 0 0-.32.32v3.86c0 .21-.16.38-.37.38h-7.4a.37.37 0 0 1-.38-.38v-7.4c0-.2.17-.37.38-.37h4.06c.17 0 .31-.14.31-.32V3.2a.32.32 0 0 0-.31-.32H3.86C2.83 2.88 2 3.72 2 4.74v7.4C2 13.17 2.83 14 3.86 14h7.4c1.02 0 1.86-.83 1.86-1.86V8.28a.32.32 0 0 0-.32-.32Z"
              fill="#262626"
            />
            <path
              d="M13.68 2h-3.37a.32.32 0 0 0-.32.32v.85c0 .17.15.31.32.31h1.16L7.1 7.84a.32.32 0 0 0 0 .45l.6.6a.32.32 0 0 0 .45 0l4.36-4.36V5.7c0 .17.14.32.31.32h.85c.18 0 .32-.15.32-.32V2.32a.32.32 0 0 0-.32-.32Z"
              fill="#262626"
            />
          </svg>
          Inbox
        </Button>
      </GridCell>
    ),
  },
];

export const SlideoutEmailStats: React.FC<SlideoutEmailStats> = props => {
  const { dialogueId, emailWidgetId, chatbotId } = props;

  const { setRef, close } = useSlideoutOnClose();

  const history = useHistory();
  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );

  const widget = widgets.find(
    w => w.type === WidgetTypes.EMAIL && w.id === emailWidgetId,
  );

  const { retrieveEmailAnalytics } = useBotAnalyticsCRUDActions();

  const [filter, setFilter] = useState('sent');

  const [busy, setBusy] = useState(false);

  const emailAnalytics = useSelector(selectEmailAnalytics);
  const stats = emailAnalytics?.by_widget?.stats;
  const pagination = emailAnalytics?.by_widget?.pagination;

  const getStats = async (page?: number, perPage?: number) => {
    if (widget && widget?.config?.id && chatbotId) {
      setBusy(true);
      try {
        await retrieveEmailAnalytics({
          botId: chatbotId,
          tab: 'by_widget',
          widgetDataEmailId: widget.config.id,
          page,
          perPage,
        });
      } catch (err) {
        errorToast('Something went wrong', 'top-left');
      } finally {
        setBusy(false);
      }
    }
  };

  const liveChatService = useLiveChatService();

  const onInboxClick = remoteUserId => {
    if (chatbotId) {
      liveChatService.send({
        type: 'LC_REMOTE_USER_ID_CHANGE',
        payload: remoteUserId,
      });

      const link = BotBuilderRoutes.liveChat.getLink(
        {
          chatbotId,
          remoteUserId,
        },
        { encode: false },
      );

      setTimeout(() => {
        history.push(link);
      }, 500);

      close();
    }
  };

  useEffect(() => {
    getStats();
  }, []);

  const config = widget?.config as unknown as EmailConfig;

  const title = (
    <>
      Email Statistics
      {config.subject && (
        <>
          {' '}
          for <span className={css.attention}>{config.subject}</span>{' '}
        </>
      )}
    </>
  );

  const gridTemplateColumns = `35% 15% 15% 20% 15%`;

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <Heading size={4}>{title}</Heading>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <div className={css.cards}>
          <div
            className={classNames(css.card, {
              [css.active]: filter === 'sent',
            })}
            onClick={() => setFilter('sent')}
          >
            <span className={css.title}>Sent emails</span>
            <span className={css.icon}>
              <SendOutlined />
            </span>

            {pagination && (
              <span className={css.value}>
                {Intl.NumberFormat().format(pagination.total) || '-'}
              </span>
            )}
          </div>
        </div>

        {busy && <Spinner standalone size={16} />}

        {!stats?.length && (
          <div className={css.empty}>
            <h1>
              No Data.
              <br />
              Here will be contacts emails
            </h1>
            <span>
              <EmptyStats />
            </span>
          </div>
        )}

        {!busy && !!stats?.length && (
          <div className={css.list}>
            <div className={css.actions}>
              {pagination && !!pagination.total && (
                <span className={css.count}>
                  {pagination.total} {pluralize('emails', pagination.total)}
                </span>
              )}

              <Button color="white" size="md" onClick={() => onInboxClick('')}>
                See responses in Inbox
              </Button>
            </div>

            <div className={css.grid}>
              <Grid className="integrations-grid">
                <GridLabels
                  className="integrations-grid__labels connections-panel__grid-labels"
                  gridTemplateColumns={gridTemplateColumns}
                >
                  {cells.map(cell => (
                    <TableLabel
                      key={cell.keyName || cell.label}
                      dataItem={cell}
                      orderByAtTheEnd
                    />
                  ))}
                </GridLabels>

                {stats.map(entry => {
                  const [data] = Object.entries(entry);
                  const [_, info] = data || [];

                  const item = { ...info, id: Math.random() };
                  return (
                    <GridRow
                      className="integrations-grid__rows-container"
                      key={info.email}
                      gridTemplateColumns={gridTemplateColumns}
                    >
                      {cells.map(({ Cell, keyName }) => (
                        <Cell
                          key={keyName}
                          item={item}
                          onInboxClick={() => onInboxClick(item.remoteId)}
                        />
                      ))}
                    </GridRow>
                  );
                })}
              </Grid>
            </div>

            {!!pagination?.total && (
              <PaginationNav
                className={css.pagination}
                pagination={pagination}
                onChangePage={page => getStats(page, pagination.perPage)}
                rangeLength={5}
                withEllipsis
              />
            )}
          </div>
        )}
      </Menu.Content>
    </Menu>
  );
};
